import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  UserPlus,
  EnvelopeSimple,
  Trash,
  CheckCircle,
  Clock,
  Users,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InviteUsers({ open, onClose }) {
  const [invites, setInvites] = useState([]);
  const [users, setUsers] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("user");
  const [sending, setSending] = useState(false);
  const [tab, setTab] = useState("invites");

  useEffect(() => {
    if (!open) return;
    fetchData();
  }, [open]);

  const fetchData = async () => {
    try {
      const [invRes, usrRes] = await Promise.all([
        axios.get(`${API}/invites`),
        axios.get(`${API}/users`),
      ]);
      setInvites(invRes.data);
      setUsers(usrRes.data);
    } catch (e) {
      toast.error("Failed to load data");
    }
  };

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSending(true);
    try {
      await axios.post(`${API}/invites`, { email, role });
      toast.success(`Invite sent to ${email}`);
      setEmail("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invite");
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (inviteId) => {
    try {
      await axios.delete(`${API}/invites/${inviteId}`);
      toast.success("Invite removed");
      fetchData();
    } catch (e) {
      toast.error("Failed to delete invite");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 sm:max-w-lg" data-testid="invite-dialog">
        <DialogHeader>
          <DialogTitle className="text-zinc-50 flex items-center gap-2">
            <Users size={20} weight="duotone" className="text-teal-400" />
            Team Management
          </DialogTitle>
          <DialogDescription className="text-zinc-400 text-xs">
            Invite users and manage team access.
          </DialogDescription>
        </DialogHeader>

        {/* Invite Form */}
        <form onSubmit={handleInvite} className="flex gap-2 items-end" data-testid="invite-form">
          <div className="flex-1">
            <Label className="text-xs text-zinc-400">Email</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="colleague@company.com"
              required
              className="mt-1 bg-zinc-950 border-zinc-800 text-sm focus:ring-1 focus:ring-teal-400"
              data-testid="invite-email"
            />
          </div>
          <div className="w-[100px]">
            <Label className="text-xs text-zinc-400">Role</Label>
            <Select value={role} onValueChange={setRole}>
              <SelectTrigger className="mt-1 bg-zinc-950 border-zinc-800 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="user">User</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            type="submit"
            disabled={sending}
            className="bg-teal-500 hover:bg-teal-600 text-zinc-950 font-semibold h-9"
            data-testid="send-invite-btn"
          >
            <UserPlus size={16} className="mr-1.5" />
            {sending ? "..." : "Invite"}
          </Button>
        </form>

        {/* Tabs */}
        <div className="flex gap-1 mt-2">
          <button
            onClick={() => setTab("invites")}
            className={`text-xs font-mono px-3 py-1.5 rounded-sm transition-colors ${
              tab === "invites" ? "bg-teal-500/10 text-teal-400" : "text-zinc-500 hover:text-zinc-300"
            }`}
          >
            Invites ({invites.length})
          </button>
          <button
            onClick={() => setTab("users")}
            className={`text-xs font-mono px-3 py-1.5 rounded-sm transition-colors ${
              tab === "users" ? "bg-teal-500/10 text-teal-400" : "text-zinc-500 hover:text-zinc-300"
            }`}
          >
            Users ({users.length})
          </button>
        </div>

        {/* Content */}
        <ScrollArea className="max-h-[300px]">
          {tab === "invites" ? (
            invites.length === 0 ? (
              <p className="text-xs text-zinc-600 text-center py-8">No invites yet</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-zinc-800">
                    <TableHead className="text-xs text-zinc-500">Email</TableHead>
                    <TableHead className="text-xs text-zinc-500">Role</TableHead>
                    <TableHead className="text-xs text-zinc-500">Status</TableHead>
                    <TableHead className="text-xs text-zinc-500 w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invites.map((inv) => (
                    <TableRow key={inv.id} className="border-zinc-800 hover:bg-zinc-800">
                      <TableCell className="text-xs font-mono text-zinc-300">
                        <div className="flex items-center gap-1.5">
                          <EnvelopeSimple size={12} className="text-zinc-500" />
                          {inv.email}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[10px] border-zinc-700">{inv.role}</Badge>
                      </TableCell>
                      <TableCell>
                        {inv.used ? (
                          <span className="text-[10px] text-emerald-400 flex items-center gap-1">
                            <CheckCircle size={11} /> Accepted
                          </span>
                        ) : (
                          <span className="text-[10px] text-amber-400 flex items-center gap-1">
                            <Clock size={11} /> Pending
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        {!inv.used && (
                          <button
                            onClick={() => handleDelete(inv.id)}
                            className="text-zinc-600 hover:text-red-400 transition-colors"
                          >
                            <Trash size={14} />
                          </button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800">
                  <TableHead className="text-xs text-zinc-500">Name</TableHead>
                  <TableHead className="text-xs text-zinc-500">Email</TableHead>
                  <TableHead className="text-xs text-zinc-500">Role</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u._id} className="border-zinc-800 hover:bg-zinc-800">
                    <TableCell className="text-xs text-zinc-300">{u.name}</TableCell>
                    <TableCell className="text-xs font-mono text-zinc-400">{u.email}</TableCell>
                    <TableCell>
                      <Badge
                        variant={u.role === "admin" ? "default" : "outline"}
                        className={`text-[10px] ${u.role === "admin" ? "bg-teal-500/20 text-teal-400 border-teal-800" : "border-zinc-700"}`}
                      >
                        {u.role}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
